/**
 * GATE-EXPERIENCE-1b — who has decided, when they were last reminded, and the
 * next automatic reminder. Remind now posts the manual reminder.
 */
import { useEffect, useState } from 'react';

import {
  getGateStatus,
  remindGateApprovers,
  type GateStatusView,
} from './phaseGates.api';

const REASON: Record<string, string> = {
  NOT_UNDER_REVIEW: 'This gate is not under review.',
  REMINDERS_OFF: 'Automatic reminders are off.',
  NO_ACTIVE_STEP: 'No approval step is active.',
  STEP_HAS_DECISION: 'The active step already has a decision.',
  ALL_STAGES_SENT: 'Every automatic reminder has been sent.',
};

function when(value: string | null): string {
  return value ? value : '—';
}

export function GateApprovalStatus({ submissionId }: { submissionId: string }) {
  const [status, setStatus] = useState<GateStatusView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    void getGateStatus(submissionId)
      .then((body) => setStatus(body))
      .catch(() => setStatus(null));
  };

  useEffect(() => {
    load();
  }, [submissionId]);

  const remind = async () => {
    setBusy(true);
    setError(null);
    try {
      await remindGateApprovers(submissionId);
      load();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      setError(data?.message || 'Could not send the reminder.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const next = status.nextAutomaticReminder;
  return (
    <section className="mt-6 rounded border border-slate-200 p-4" data-testid="gate-approval-status">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Approvals</h2>
      {status.steps.map((step) => (
        <div key={step.stepId} className="mt-3">
          <p className="text-sm font-medium text-slate-900">{step.name}</p>
          <ul className="mt-1 space-y-1">
            {step.approvers.map((person) => (
              <li key={person.userId} className="text-sm text-slate-700" data-testid={`gate-approver-${person.userId}`}>
                {person.name ?? 'Approver'} · {person.decision ?? 'Pending'}
                {person.lastRemindedAt ? ` · reminded ${person.lastRemindedAt}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="mt-3 text-sm text-slate-600" data-testid="gate-next-reminder">
        {next
          ? `Next reminder ${when(next.dueAt)} (${next.stage})`
          : REASON[status.nextAutomaticReminderReason ?? ''] ?? 'No automatic reminder.'}
      </p>
      {status.submissionStatus === 'SUBMITTED' ? (
        <button
          type="button"
          className="mt-2 rounded border px-2 py-1 text-sm"
          disabled={busy}
          onClick={() => void remind()}
        >
          Remind now
        </button>
      ) : null}
      {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
    </section>
  );
}
